import type { FC, ReactNode } from 'react'
import type { TargetAndTransition, Transition, Variants } from 'motion/react'

import { Fragment } from 'react'
import { motion } from 'motion/react'

export type SplitEffect = 'flip' | 'slide' | 'slide-left' | 'blur' | 'blur-rise' | 'rise'

type SplitTag = 'span' | 'p' | 'h1' | 'h2' | 'h3' | 'h4'

interface SplitTextProps {
    text: string
    effect?: SplitEffect
    by?: 'char' | 'word'
    trigger?: 'mount' | 'view'
    delay?: number
    stagger?: number
    as?: SplitTag
    className?: string
    accessible?: boolean
    amount?: number
}

const motionTags = {
    span: motion.span,
    p: motion.p,
    h1: motion.h1,
    h2: motion.h2,
    h3: motion.h3,
    h4: motion.h4
}

const hiddenStates: Record<SplitEffect, TargetAndTransition> = {
    flip: { opacity: 0.001, rotateY: 90, transformPerspective: 600 },
    slide: { opacity: 0.001, x: 108 },
    'slide-left': { opacity: 0.001, x: -108 },
    blur: { opacity: 0.001, filter: 'blur(2px)' },
    'blur-rise': { opacity: 0.001, filter: 'blur(2px)', y: 40 },
    rise: { opacity: 0.001, y: 20 }
}

const shownStates: Record<SplitEffect, TargetAndTransition> = {
    flip: { opacity: 1, rotateY: 0, transformPerspective: 600 },
    slide: { opacity: 1, x: 0 },
    'slide-left': { opacity: 1, x: 0 },
    blur: { opacity: 1, filter: 'blur(0px)' },
    'blur-rise': { opacity: 1, filter: 'blur(0px)', y: 0 },
    rise: { opacity: 1, y: 0 }
}

const charTransition: Transition = {
    default: { type: 'spring', bounce: 0.15, duration: 0.9 },
    opacity: { duration: 0.45, ease: [0.22, 1, 0.36, 1] },
    filter: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
}

export const SplitText: FC<SplitTextProps> = ({
    text,
    effect = 'slide',
    by = 'char',
    trigger = 'view',
    delay = 0,
    stagger,
    as = 'span',
    className,
    accessible = true,
    amount = 0.4
}): ReactNode => {
    const Tag = motionTags[as] as typeof motion.span
    const step = stagger ?? (by === 'char' ? 0.025 : 0.035)
    const words = text.split(' ')

    const container: Variants = {
        hidden: {},
        visible: {
            transition: {
                staggerChildren: step,
                delayChildren: delay
            }
        }
    }

    const piece: Variants = {
        hidden: hiddenStates[effect],
        visible: { ...shownStates[effect], transition: charTransition }
    }

    const triggerProps = trigger === 'mount'
        ? { animate: 'visible' }
        : { whileInView: 'visible', viewport: { once: true, amount } }

    return (
        <Tag
            className={className}
            initial='hidden'
            variants={container}
            {...triggerProps}
        >
            {accessible && (
                <span className='sr-only'>
                    {text}
                </span>
            )}

            <span aria-hidden='true'>
                {words.map((word, wordIndex) => (
                    <Fragment key={`${word}-${wordIndex}`}>
                        {by === 'char' ? (
                            <span className='whitespace-nowrap'>
                                {Array.from(word).map((char, charIndex) => (
                                    <motion.span
                                        key={`${char}-${charIndex}`}
                                        className='inline-block'
                                        variants={piece}
                                    >
                                        {char}
                                    </motion.span>
                                ))}
                            </span>
                        ) : (
                            <motion.span
                                className='inline-block'
                                variants={piece}
                            >
                                {word}
                            </motion.span>
                        )}

                        {wordIndex < words.length - 1 ? ' ' : null}
                    </Fragment>
                ))}
            </span>
        </Tag>
    )
}
